import { useCallback, useEffect, useState } from 'react';

import { getAppVersionDetail, installApp } from '../../api/app-store-api';

import type { AppStoreInstallResponse, AppStoreVersionDetailResponse } from '../../api/app-store-api';

type LoadState =
  | { status: 'loading' }
  | { status: 'loaded'; version: AppStoreVersionDetailResponse }
  | { status: 'error'; errorKey: 'failedToLoad' | 'versionNotPublished'; errorValues?: Record<string, string> };

type InstallState =
  | { status: 'idle' }
  | { status: 'installing' }
  | { status: 'installed'; result: AppStoreInstallResponse }
  | { status: 'error' };

export const useAppVersionDetail = (appStoreAppVersionGuid: string | null) => {
  const [loadState, setLoadState] = useState<LoadState>({ status: 'loading' });
  const [installState, setInstallState] = useState<InstallState>({ status: 'idle' });

  useEffect(() => {
    if (!appStoreAppVersionGuid) return;

    let cancelled = false;
    getAppVersionDetail(appStoreAppVersionGuid)
      .then((version) => {
        if (cancelled) return;
        if (version.status !== 'published') {
          setLoadState({ status: 'error', errorKey: 'versionNotPublished', errorValues: { status: version.status } });
          return;
        }
        setLoadState({ status: 'loaded', version });
      })
      .catch(() => {
        if (!cancelled) {
          setLoadState({ status: 'error', errorKey: 'failedToLoad' });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [appStoreAppVersionGuid]);

  const handleInstall = useCallback(() => {
    if (loadState.status !== 'loaded') return;
    const { version } = loadState;
    setInstallState({ status: 'installing' });
    installApp(version.appStoreAppGuid, version.appStoreAppVersionGuid)
      .then((result) => {
        setInstallState({ status: 'installed', result });
      })
      .catch(() => {
        setInstallState({ status: 'error' });
      });
  }, [loadState]);

  return { loadState, installState, handleInstall };
};
